/**
 * SearchBar Component
 * 
 * Search across conversations with full-text search.
 * Shows results with highlighted snippets.
 */

import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import '../styles/SearchBar.css';

export default function SearchBar({ onSelectResult }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 2) {
      setResults([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      search(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const search = async (q) => {
    setIsSearching(true);
    try {
      const response = await fetch(
        `/conversations/search?q=${encodeURIComponent(q)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setResults(data.results);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelect = (conversationId) => {
    setShowResults(false);
    setQuery('');
    if (onSelectResult) {
      onSelectResult(conversationId);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowResults(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="search-bar-container">
      <div className="search-input-wrapper">
        <span className="search-icon">🔍</span>
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder="Search conversations..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setShowResults(true)}
          onKeyDown={handleKeyDown}
        />
        {isSearching && <span className="search-spinner">⟳</span>}
        {query && (
          <button 
            className="search-clear"
            onClick={() => setQuery('')}
          >
            ×
          </button>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div className="search-results">
          {results.map(result => (
            <div
              key={result.conversation_id}
              className="search-result"
              onClick={() => handleSelect(result.conversation_id)}
            >
              <div className="search-result-snippet" 
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatSnippet(result.snippet)) }}
              />
              <div className="search-result-meta">
                <span className={`status-badge ${result.status}`}>
                  {result.status}
                </span>
                <span className="search-result-date">
                  {formatDate(result.matched_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showResults && query.length >= 2 && results.length === 0 && !isSearching && (
        <div className="search-results">
          <div className="search-no-results">
            No results found for "{query}"
          </div>
        </div>
      )}
    </div>
  );
}

function formatSnippet(snippet) {
  // Replace ** markers with <mark> tags
  return snippet
    .replace(/\*\*/g, '</mark>')
    .replace(/<\/mark>([^<]*)<\/mark>/g, '<mark>$1</mark>');
}

function formatDate(isoDate) {
  if (!isoDate) return '';
  // Ensure UTC interpretation: append 'Z' if no timezone indicator present
  let dateString = isoDate;
  if (!dateString.endsWith('Z') && !dateString.match(/[+-]\d{2}(:\d{2})?$/)) {
    dateString = dateString + 'Z';
  }
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 86400000) { // Less than 1 day
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diff < 604800000) { // Less than 1 week
    return date.toLocaleDateString([], { weekday: 'short' });
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
}
